<script setup lang="ts">
import { computed } from 'vue';
import type { ApiImage } from '@/types/images';
import PersonBlock from './PersonBlock.vue';

export type PersonValue = {
  name?: string | null;
  title?: string | null;
  department?: string | null;
  description?: string | null;
  image?: ApiImage;
};

type PeoplePreviewPayload = {
  title?: string | null;
  people?: PersonValue[];
};

const props = withDefaults(
  defineProps<{
    title?: string | null;
    people?: PersonValue[];
    /** Block-picker iframe: JSON from `get_api_representation`. Query key `payload`. */
    payload?: string | null;
  }>(),
  { title: null, people: () => [] }
);

const previewData = computed((): PeoplePreviewPayload | null => {
  const raw = props.payload?.trim();
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as unknown;
    if (p && typeof p === 'object') return p as PeoplePreviewPayload;
    return null;
  } catch {
    return null;
  }
});

const sectionTitle = computed(() => {
  if (previewData.value) {
    const t = previewData.value.title;
    if (typeof t === 'string' && t.trim()) return t.trim();
    return 'Team Members';
  }
  return props.title?.trim() || 'Team Members';
});

const personList = computed(() => {
  if (previewData.value?.people != null) {
    return Array.isArray(previewData.value.people) ? previewData.value.people : [];
  }
  return Array.isArray(props.people) ? props.people : [];
});
</script>

<template>
  <section v-if="personList.length > 0" class="people-block">
    <h2 class="people-block__title">{{ sectionTitle }}</h2>
    <ul class="people-block__grid" aria-label="Team members">
      <li
        v-for="(person, index) in personList"
        :key="index"
        class="people-block__grid-item"
      >
        <PersonBlock
          :name="person.name"
          :title="person.title"
          :department="person.department"
          :description="person.description"
          :image="person.image"
        />
      </li>
    </ul>
  </section>
</template>

<style scoped>
.people-block {
  margin-bottom: 1.5rem;
}

.people-block__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.75rem;
}

/* Grid: 1 col mobile, 2 cols sm, 3 cols lg (matches prototype) */
.people-block__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.people-block__grid-item {
  margin: 0;
}

@media (min-width: 640px) {
  .people-block__title {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .people-block__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (min-width: 1024px) {
  .people-block__title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .people-block__grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
}
</style>
